import { useState } from "react";
import { Minus, Plus } from "lucide-react";
import { cn } from "./ui";
import { t } from "@/lib/i18n";
import { WORKFLOW_STEP_LIMIT, type WorkflowStep } from "@/mocks/workflows";

/* Схема рабочего процесса: узлы соединены линиями, между ними могут быть подписи веток.
   Панель зума слева сверху, внизу — счётчик шагов. */

const NODE_META: Record<string, { icon: string; iconTone: string; border: string }> = {
  trigger: { icon: "ri-flashlight-line", iconTone: "text-red-500", border: "border-red-500/35" },
  message: { icon: "ri-send-plane-line", iconTone: "text-primary", border: "border-border" },
  team: { icon: "ri-team-line", iconTone: "text-primary", border: "border-border" },
  task: { icon: "ri-send-plane-line", iconTone: "text-primary", border: "border-border" },
  condition: { icon: "ri-git-branch-line", iconTone: "text-amber-600", border: "border-border" },
  delay: { icon: "ri-time-line", iconTone: "text-primary", border: "border-border" },
};

function Node({ step }: { step: WorkflowStep }) {
  const meta = NODE_META[step.kind] ?? NODE_META.message;
  return (
    <div className={cn("rounded-btn border bg-card px-2.5 py-2", meta.border)}>
      <p className="flex items-center gap-1.5 text-[10.5px] font-semibold leading-snug">
        <i className={cn(meta.icon, "flex-none text-[11px] leading-none", meta.iconTone)} aria-hidden />
        <span className="min-w-0 truncate">{step.title}</span>
      </p>
      {step.field ? (
        <p className="mt-1 flex items-center justify-between gap-1 rounded-[4px] border border-border bg-background/60 px-1.5 py-[3px] text-[9px] text-foreground/70">
          <span className="min-w-0 truncate">{step.label}</span>
          <i className="ri-arrow-down-s-line flex-none text-[10px] leading-none text-muted-foreground" aria-hidden />
        </p>
      ) : step.label ? (
        <p className="mt-1 text-[9px] leading-none text-muted-foreground">{step.label}</p>
      ) : null}
      {step.text ? (
        <p className={cn("mt-0.5 text-[9px] leading-relaxed text-foreground/70", step.kind !== "trigger" && "italic")}>{step.text}</p>
      ) : null}
    </div>
  );
}

type Props = {
  steps: WorkflowStep[];
  className?: string;
  /** подпись внизу слева вместо счётчика шагов */
  footerNote?: string;
};

export default function WorkflowCanvas({ steps, className, footerNote }: Props) {
  const [zoom, setZoom] = useState(1);
  const stepsCount = steps.filter((s) => s.kind !== "trigger").length;

  return (
    <div
      className={cn("relative overflow-hidden rounded-btn border border-border bg-background/40", className)}
      style={{
        backgroundImage: "radial-gradient(rgb(var(--f-fg) / 0.14) 1px, transparent 1px)",
        backgroundSize: "16px 16px",
      }}
    >
      <div className="absolute inset-y-3 start-2 z-10 flex flex-col gap-1 rounded-btn border border-border bg-card/95 p-1 backdrop-blur">
        <button
          type="button"
          onClick={() => setZoom((z) => Math.min(1.4, Number((z + 0.1).toFixed(2))))}
          aria-label={t("Приблизить схему")}
          title={t("Приблизить")}
          className="grid size-6 cursor-pointer place-items-center rounded-[4px] text-muted-foreground transition hover:bg-muted hover:text-foreground"
        >
          <Plus className="size-3.5" aria-hidden />
        </button>
        <button
          type="button"
          onClick={() => setZoom((z) => Math.max(0.7, Number((z - 0.1).toFixed(2))))}
          aria-label={t("Отдалить схему")}
          title={t("Отдалить")}
          className="grid size-6 cursor-pointer place-items-center rounded-[4px] text-muted-foreground transition hover:bg-muted hover:text-foreground"
        >
          <Minus className="size-3.5" aria-hidden />
        </button>
        <button
          type="button"
          onClick={() => setZoom(1)}
          aria-label={t("Вписать схему в окно")}
          title={t("Вписать в окно")}
          className="grid size-6 cursor-pointer place-items-center rounded-[4px] text-muted-foreground transition hover:bg-muted hover:text-foreground"
        >
          <i className="ri-fullscreen-line text-[12px] leading-none" aria-hidden />
        </button>
      </div>

      <div className="max-h-[30rem] overflow-auto px-10 py-5">
        <div className="mx-auto w-full max-w-[17.5rem] transition-transform duration-200" style={{ transform: `scale(${zoom})`, transformOrigin: "top center" }}>
          {steps.map((step, i) => (
            <div key={step.id}>
              {i > 0 ? (
                <div className="relative mx-auto flex h-6 w-full items-center justify-center">
                  <span className="absolute inset-y-0 start-1/2 w-px -translate-x-1/2 bg-border" aria-hidden />
                  {step.connector ? (
                    <span className="relative rounded-full border border-emerald-500/40 bg-emerald-500/10 px-1.5 py-[2px] text-[8.5px] font-semibold text-emerald-600">
                      {step.connector}
                    </span>
                  ) : null}
                </div>
              ) : null}
              <Node step={step} />
            </div>
          ))}
          {steps.length === 0 ? (
            <p className="rounded-btn border border-dashed border-border px-3 py-6 text-center text-[10.5px] text-muted-foreground">
              {t("Добавьте триггер, чтобы процесс запускался")}
            </p>
          ) : null}
        </div>
      </div>

      <div className="flex items-center justify-between gap-2 border-t border-border/70 px-3 py-2">
        <p className="text-[10px] text-muted-foreground">{footerNote ?? t("Шаги: {n} / {limit}", { n: stepsCount, limit: WORKFLOW_STEP_LIMIT })}</p>
        <span className="flex items-center gap-1.5 text-[10px] text-muted-foreground">
          <i className="ri-drag-move-line text-[11px] leading-none" aria-hidden />
          {t("Схема масштабируется кнопками слева")}
        </span>
      </div>
    </div>
  );
}